import type { AttributionPayload } from './attribution';
import { firstToolEventId, pageViewEventId, signupEventId, viewToolEventId } from './events';

export interface TrackingRpc {
  (fn: string, args: Record<string, unknown>): Promise<{ data: unknown; error: { message: string } | null }>;
}

function asRecord(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data as Record<string, unknown>;
}

export async function claimCanonicalEvent(
  rpc: TrackingRpc,
  eventId: string,
  consent: { analytics: boolean; advertising: boolean },
  acquisition?: AttributionPayload | null,
): Promise<Record<string, unknown> | null> {
  try {
    const { data, error } = await rpc('claim_conversion_event', {
      p_event_id: eventId,
      p_consent: consent,
      p_acquisition: acquisition ?? {},
    });
    if (error) return null;
    return asRecord(data);
  } catch {
    return null;
  }
}

export async function claimSignup(
  rpc: TrackingRpc,
  userId: string,
  consent: { analytics: boolean; advertising: boolean },
  acquisition?: AttributionPayload | null,
): Promise<Record<string, unknown> | null> {
  return claimCanonicalEvent(rpc, signupEventId(userId), consent, acquisition);
}

/** Inserts the once-per-user first successful job. Does not change credit accounting. */
export async function notifyToolSuccess(rpc: TrackingRpc, app: string): Promise<{ inserted: boolean; eventId: string | null }> {
  try {
    const { data, error } = await rpc('record_first_tool_use', { p_app: app });
    if (error) return { inserted: false, eventId: null };
    const row = asRecord(data);
    return {
      inserted: row?.inserted === true,
      eventId: typeof row?.event_id === 'string' ? row.event_id : null,
    };
  } catch {
    return { inserted: false, eventId: null };
  }
}

export async function recordConsentedVisit(
  rpc: TrackingRpc,
  input: { sessionId: string; path: string; app: string; acquisition?: AttributionPayload | null },
): Promise<void> {
  try {
    await rpc('record_consented_visit', {
      p_event_id: pageViewEventId(input.sessionId, input.path),
      p_app: input.app,
      p_path: input.path.slice(0, 500),
      p_acquisition: input.acquisition ?? {},
    });
  } catch {
    /* reporting must not affect the page */
  }
}

export async function recordViewTool(
  rpc: TrackingRpc,
  input: { sessionId: string; app: string },
): Promise<void> {
  try {
    await rpc('record_view_tool', {
      p_event_id: viewToolEventId(input.app, input.sessionId),
      p_app: input.app,
    });
  } catch {
    /* ignore */
  }
}

export { firstToolEventId };
